import { clsx } from 'clsx';
import { forwardRef, useId } from 'react';
import type { TextareaHTMLAttributes, ReactNode } from 'react';

type TextAreaProps = {
  label: string;
  hint?: ReactNode;
  error?: string;
  showCounter?: boolean;
  containerClassName?: string;
} & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'>;

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, hint, error, showCounter, maxLength, value, containerClassName, className, rows = 3, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = `ta-${generatedId}`;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;

  const currentLength = typeof value === 'string' ? value.length : 0;

  return (
    <div className={clsx('flex flex-col gap-2', containerClassName)}>
      <div className="flex items-baseline justify-between gap-3">
        <label
          htmlFor={inputId}
          className="font-mono text-xs uppercase tracking-stamp text-champagne"
        >
          {label}
        </label>
        {showCounter && typeof maxLength === 'number' ? (
          <span
            className={clsx(
              'font-mono text-[10px] tabular-nums',
              currentLength > maxLength * 0.9 ? 'text-error' : 'text-neutral-300',
            )}
          >
            {currentLength}/{maxLength}
          </span>
        ) : null}
      </div>

      <textarea
        ref={ref}
        id={inputId}
        value={value}
        maxLength={maxLength}
        rows={rows}
        aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
        aria-invalid={error ? true : undefined}
        className={clsx(
          'w-full resize-none rounded-md border bg-night/40 px-4 py-3 text-base text-parchment',
          'placeholder:text-neutral-300/50',
          'transition-colors duration-fast',
          'focus:outline-none focus:ring-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold',
          error
            ? 'border-error/70 focus:border-error'
            : 'border-gold/20 focus:border-gold',
          className,
        )}
        {...rest}
      />

      {hint && !error ? (
        <p id={hintId} className="text-xs italic leading-relaxed text-champagne/60">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p
          id={errorId}
          role="alert"
          aria-live="polite"
          className="flex items-start gap-2 text-xs text-error"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
});
